import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Table,
} from 'antd';
import { useRequest } from 'ahooks';
import { RechargeOrderListParams } from '@/types/order';
import { getRechargeAppleList } from '@/api/order';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';
import {
  formatCampaign,
  formatChannel,
  formatRegisterPlatform,
} from '@/utils/attribution';

// 订单状态枚举
export enum OrderStatus {
  PAID = 1,
  PAYMENT_FAILED = 2,
  REQUEST_REFUND = 3,
  REFUNDED = 4,
  REFUND_FAILED = 5,
}

const statusMap = new Map([
  [OrderStatus.PAID, '已支付'],
  [OrderStatus.PAYMENT_FAILED, '支付失败'],
  [OrderStatus.REQUEST_REFUND, '申请退款中'],
  [OrderStatus.REFUNDED, '已退款'],
  [OrderStatus.REFUND_FAILED, '退款失败'],
]);

enum EnvStatus {
  TEST,
  PROD
}

const envMap = new Map([
  [EnvStatus.TEST, '测试'],
  [EnvStatus.PROD, '正式'],
]);

// 用户来源映射（与会员订阅查询一致）
const SourceTypeMap = new Map<number, string>([
  [1, 'suppart'],
  [2, 'appleId'],
  [3, 'facebook'],
  [4, 'googleId'],
  [5, 'LineId'],
]);

const pageSize = 20;

const OrderPayPage = () => {
  const [form] =
    Form.useForm<Omit<RechargeOrderListParams, 'currentPage' | 'pageSize'>>();
  const [messageApi, contextHolder] = message.useMessage();
  // const [page, setPage] = useState(1);

  const {
    data,
    loading: isLoading,
    refresh: refetch,
    run
  } = useRequest(
    async (currentPage = 1) => {
      const formParams = form.getFieldsValue();
      if (formParams.createTime) {
        formParams.createTimeStart = dayjs(formParams.createTime[0])
          .startOf('day')
          .valueOf();
        formParams.createTimeEnd = dayjs(formParams.createTime[1])
          .endOf('day')
          .valueOf();

        delete formParams.createTime;
      }

      const params: RechargeOrderListParams = {
        ...formParams,
        currentPage,
        pageSize,
      };
      const resp = await getRechargeAppleList(params);
      if (resp.status !== 200) {
        return {
          total: 0,
          currentPage: 0,
          pageSize: 0,
          records: [],
        };
      }
      return resp.data;
    }
  );

  const page = useMemo(() => {
    return data?.currentPage ?? 1
  }, [data]);

  const handleSearch = () => {
    // setPage(1);
    void run();
  };

  const handleReset = () => {
    form.resetFields();
    // setPage(1);
    void run();
  };

  const columns: ColumnsType<any> = [
    {
      title: '订单交易流水号',
      dataIndex: 'transactionId',
      width: 120,
    },
    {
      title: '订单日期',
      dataIndex: 'createTime',
      render: (value: string) =>
        value ? dayjs(value).format(FORMAT_TIME) : '-',
    },
    {
      title: '订单金额',
      dataIndex: 'amount',
    },
    {
      title: '币种',
      dataIndex: 'currency',
      render: (value: string) => value || '-',
    },
    {
      title: '购买类型',
      dataIndex: 'amout',
      render: () => '充值金币',
    },
    {
      title: '购买前数量',
      dataIndex: 'beforeNum',
    },
    {
      title: '本次购买数量',
      dataIndex: 'num',
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      width: 100,
      render: (value: number) => statusMap.get(value),
    },
    {
      title: '用户Supp ID',
      dataIndex: 'suppId',
      width: 100,
    },
    {
      title: '用户名称',
      dataIndex: 'userName',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: '用户身份',
      dataIndex: 'vipName',
      width: 120,
      render: (value: string) => {
        if (!value) return '-';
        return value.replace(/Plus/g, 'Pro');
      },
    },
    {
      title: '登录方式',
      dataIndex: 'sourceType',
      width: 100,
      render: (value: number) => (value ? SourceTypeMap.get(value) || '-' : '-'),
    },
    {
      title: '注册来源',
      dataIndex: 'deviceType',
      width: 100,
      render: (value: number) => formatRegisterPlatform(value),
    },
    {
      title: '注册时间',
      dataIndex: 'registerTime',
      width: 180,
      render: (value: string) =>
        value ? dayjs(value).format(FORMAT_TIME) : '-',
    },
    {
      title: '渠道来源',
      dataIndex: 'afSource',
      width: 120,
      render: (value: string, record: any) =>
        formatChannel(value, record.deviceType),
    },
    {
      title: '归因数据',
      dataIndex: 'afCampaign',
      width: 120,
      render: (value: string) => formatCampaign(value),
    },
  ];

  const searchForm = (
    <Form
      form={form}
      size="small"
      onFinish={handleSearch}
      style={{ width: '100%' }}
      initialValues={{
        orderStatus: '',
      }}
    >
      <Row gutter={[16, 8]}>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="suppId"
            label="Supp ID"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Input allowClear placeholder="请输入Supp ID" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="transactionId"
            label="订单号"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Input allowClear placeholder="请输入订单号" />
          </Form.Item>
        </Col>
        {/* <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="vipName"
            label="用户身份"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Input allowClear placeholder="请输入用户身份" />
          </Form.Item>
        </Col> */}
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="status"
            label="订单状态"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Select
              allowClear
              placeholder="请选择订单状态"
              options={[
                { label: '全部', value: '' },
                ...[...statusMap.keys()].map((key) => ({
                  label: statusMap.get(key),
                  value: key,
                })),
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="createTime"
            label="查询日期"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <DatePicker.RangePicker />
          </Form.Item>
        </Col>
        {/* <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="sourceType"
            label="登录方式"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Select
              allowClear
              placeholder="请选择登录方式"
              options={[
                { label: 'suppart', value: 1 },
                { label: 'appleId', value: 2 },
                { label: 'facebook', value: 3 },
                { label: 'googleId', value: 4 },
                { label: 'LineId', value: 5 },
              ]}
            />
          </Form.Item>
        </Col> */}
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="env"
            label="环境"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Select
              allowClear
              placeholder="请选择订单环境"
              options={[
                ...[...envMap.keys()].map((key) => ({
                  label: envMap.get(key),
                  value: key,
                })),
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row justify="end" style={{ marginTop: 24, marginBottom: 24 }}>
        <Col>
          <Space size="middle">
            <Button onClick={handleReset}>重置</Button>
            <Button type="primary" htmlType="submit">
              搜索
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );

  return (
    <div className="order-page-container">
      {contextHolder}
      {searchForm}

      <Table
        loading={isLoading}
        columns={columns}
        dataSource={data?.records}
        rowKey="orderId"
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize,
          total: data?.total,
          onChange: (page) => run(page),
          showSizeChanger: false,
        }}
      />
    </div>
  );
};

export default OrderPayPage;
